/**
 * Role Play Live (CRM web) — text-mode practice session.
 *
 * Browsers can't stream PCM to AAI the way Electron does, so this page
 * uses the text path: rep types a message → server injects it as a
 * transcript segment → avatar reply + TTS stream back over WS.
 *
 * TTS audio arrives as `avatar_audio` MP3 frames and plays via the same
 * concatenate-chunks-into-Blob pattern the desktop app uses.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Send, StopCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTY_COLOR = {
    friendly: '#1DA063', standard: '#3B82F6',
    hard: '#F2A423', nightmare: '#E5484D',
};

function b64ToBytes(b64) {
    const bin = atob(b64);
    const len = bin.length;
    const out = new Uint8Array(len);
    for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i);
    return out;
}

export default function RolePlayLive() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();

    const [session, setSession] = useState(null);
    const [avatars, setAvatars] = useState([]);
    const [lines, setLines] = useState([]);
    const [speakingAvatarId, setSpeakingAvatarId] = useState(null);
    const [connected, setConnected] = useState(false);
    const [ending, setEnding] = useState(false);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [elapsed, setElapsed] = useState(0);

    const wsRef = useRef(null);
    const audioQueueRef = useRef({});  // { [avatar_id]: Uint8Array[] }
    const audioElRef = useRef(null);
    const transcriptEndRef = useRef(null);
    const timerRef = useRef(null);

    // Load session via HTTP
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/role-play/sessions/${sessionId}`, {
                    headers: getAuthHeader(),
                });
                setSession(res.data);
                setAvatars(res.data.avatars || []);
            } catch (e) {
                toast.error('Failed to load session');
                navigate('/practice/role-play');
            }
        })();
    }, [sessionId, getAuthHeader, navigate]);

    // Connect WS
    useEffect(() => {
        if (!session) return;
        const token = localStorage.getItem('crm_token');
        if (!token) {
            toast.error('Not authenticated');
            return;
        }
        const wsUrl = API.replace(/^http/, 'ws').replace(/\/api$/, '');
        const url = `${wsUrl}/api/ws/role-play/${sessionId}?token=${token}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => setConnected(true);
        ws.onclose = () => setConnected(false);
        ws.onerror = () => console.warn('WS error');
        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                handleMessage(msg);
            } catch (e) {
                console.warn('bad WS frame', e);
            }
        };

        return () => {
            try { ws.close(); } catch {}
            wsRef.current = null;
        };
        // eslint-disable-next-line
    }, [session, sessionId]);

    // Elapsed timer
    useEffect(() => {
        timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
        return () => clearInterval(timerRef.current);
    }, []);

    // Auto-scroll
    useEffect(() => {
        transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [lines.length]);

    const handleMessage = useCallback((msg) => {
        switch (msg.type) {
            case 'session_context':
                if (msg.avatars) setAvatars(msg.avatars);
                break;
            case 'transcript': {
                const seg = msg.segment;
                if (!seg?.text) break;
                setLines(prev => [...prev, {
                    speaker: seg.speaker,
                    speaker_name: seg.speaker_name,
                    text: seg.text,
                }]);
                break;
            }
            case 'avatar_reply': {
                const seg = msg.segment;
                if (!seg?.text) break;
                setLines(prev => [...prev, {
                    speaker: seg.speaker,
                    speaker_name: msg.avatar_name || seg.speaker_name,
                    text: seg.text,
                }]);
                setSending(false);
                break;
            }
            case 'avatar_audio': {
                const { avatar_id, audio_b64, final } = msg;
                if (!audioQueueRef.current[avatar_id]) audioQueueRef.current[avatar_id] = [];
                if (audio_b64) {
                    try { audioQueueRef.current[avatar_id].push(b64ToBytes(audio_b64)); } catch {}
                }
                if (final) {
                    const chunks = audioQueueRef.current[avatar_id];
                    audioQueueRef.current[avatar_id] = [];
                    if (chunks && chunks.length) {
                        const total = chunks.reduce((s, c) => s + c.byteLength, 0);
                        const merged = new Uint8Array(total);
                        let offset = 0;
                        for (const c of chunks) { merged.set(c, offset); offset += c.byteLength; }
                        const blob = new Blob([merged], { type: 'audio/mpeg' });
                        const url = URL.createObjectURL(blob);
                        const audio = new Audio(url);
                        audioElRef.current = audio;
                        setSpeakingAvatarId(avatar_id);
                        audio.addEventListener('ended', () => {
                            URL.revokeObjectURL(url);
                            setSpeakingAvatarId(null);
                        });
                        audio.addEventListener('error', () => {
                            URL.revokeObjectURL(url);
                            setSpeakingAvatarId(null);
                        });
                        audio.play().catch(() => setSpeakingAvatarId(null));
                    }
                }
                break;
            }
            case 'debrief_ready':
                toast.success('Debrief ready');
                navigate(`/practice/role-play/sessions/${msg.session_id || sessionId}`);
                break;
            case 'status':
                if (msg.message) toast.info(msg.message);
                break;
            case 'error':
                toast.error(msg.message || 'Role play error');
                break;
            default: break;
        }
    }, [navigate, sessionId]);

    const sendText = () => {
        const text = input.trim();
        if (!text || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        wsRef.current.send(JSON.stringify({ type: 'rep_text', text }));
        setInput('');
        setSending(true);
    };

    const endSession = async () => {
        if (ending) return;
        setEnding(true);
        try {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'command', action: 'end_session' }));
            }
            setTimeout(async () => {
                try {
                    await axios.post(`${API}/role-play/sessions/${sessionId}/end`, {}, {
                        headers: getAuthHeader(),
                    });
                } catch {}
                navigate(`/practice/role-play/sessions/${sessionId}`);
            }, 2500);
        } catch (e) {
            toast.error('Failed to end session');
            setEnding(false);
        }
    };

    const difficulty = session?.difficulty || 'standard';
    const diffColor = DIFFICULTY_COLOR[difficulty] || '#3B82F6';
    const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

    if (!session) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-64px)]">
            {/* Practice ribbon */}
            <div
                className="flex items-center gap-3 px-6 py-2.5 text-white flex-shrink-0"
                style={{ background: 'linear-gradient(90deg, #2d1a6e, #4527A0)' }}
            >
                <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <Sparkles className="w-3.5 h-3.5" style={{ color: '#D4C7FF' }} />
                    <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#D4C7FF' }}>
                        Role Play
                    </span>
                </div>
                <span className="text-xs opacity-80 hidden sm:inline">
                    Rehearsing with AI avatars — everything recorded for debrief
                </span>
                <div className="flex-1" />
                <span className="text-xs font-mono">{fmt(elapsed)}</span>
                <div className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase">
                    <span className="w-2 h-2 rounded-full" style={{ background: diffColor }} />
                    {difficulty}
                </div>
                <button
                    onClick={endSession}
                    disabled={ending}
                    className="flex items-center gap-1.5 px-3 py-1 rounded bg-white/15 hover:bg-white/25 text-xs font-semibold disabled:opacity-50"
                >
                    <StopCircle className="w-3.5 h-3.5" />
                    {ending ? 'Ending…' : 'End & debrief'}
                </button>
            </div>

            {/* Main split: avatar rail + transcript */}
            <div className="flex flex-1 min-h-0">
                {/* Avatar rail */}
                <div className="w-64 border-r border-stone-200 bg-stone-50 p-4 flex-shrink-0 overflow-y-auto">
                    <div className="text-[10px] font-bold tracking-widest uppercase text-stone-500 mb-3">
                        AI Avatars · {connected ? 'Live' : 'Connecting…'}
                    </div>
                    <div className="space-y-2">
                        {avatars.map(a => {
                            const speaking = speakingAvatarId === a.avatar_id;
                            return (
                                <div
                                    key={a.avatar_id}
                                    className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all ${speaking ? 'bg-violet-50 border-violet-400 shadow' : 'bg-white border-stone-200'}`}
                                    style={speaking ? { boxShadow: '0 0 12px rgba(142,122,245,0.4)' } : {}}
                                >
                                    <div
                                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                        style={{ background: `linear-gradient(135deg, ${a.avatar_color}, ${a.avatar_color}88)` }}
                                    >{a.avatar_initials}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-bold truncate">{a.name}</div>
                                        <div className="text-[10px] text-stone-500">
                                            {a.title}{speaking && ' · Speaking'}
                                        </div>
                                    </div>
                                    {speaking && (
                                        <div className="flex gap-0.5 items-center">
                                            {[0.4, 0.9, 0.6, 0.8].map((h, j) => (
                                                <div
                                                    key={j}
                                                    className="rounded-sm"
                                                    style={{
                                                        width: 2,
                                                        height: 4 + h * 10,
                                                        background: '#5D3FD3',
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Transcript + input */}
                <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-white">
                    <div className="px-5 py-2.5 border-b border-stone-200 text-[10px] font-bold tracking-widest uppercase text-stone-500 flex items-center gap-2">
                        Live transcript
                        {connected && (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] normal-case tracking-normal">
                                Connected
                            </span>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        {lines.length === 0 && (
                            <div className="text-sm text-stone-400 text-center py-8">
                                Type your opening question below. The avatars will reply in persona.
                            </div>
                        )}
                        {lines.map((l, i) => {
                            const mine = l.speaker === 'rep';
                            const avatarId = l.speaker?.split?.(':')[1];
                            const av = avatars.find(a => a.avatar_id === avatarId);
                            return (
                                <div key={i} className="mb-4">
                                    <div
                                        className="text-[11px] font-bold tracking-wide mb-1"
                                        style={{ color: mine ? '#5D3FD3' : (av?.avatar_color || '#111113') }}
                                    >{mine ? 'You' : l.speaker_name}</div>
                                    <div
                                        className="text-sm leading-relaxed text-stone-900 pl-3"
                                        style={{
                                            borderLeft: `2px solid ${mine ? '#8E7AF5' : (av?.avatar_color || '#D6D3C8')}`,
                                        }}
                                    >{l.text}</div>
                                </div>
                            );
                        })}
                        {sending && (
                            <div className="text-xs text-stone-400 italic flex items-center gap-1.5">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Avatar is thinking…
                            </div>
                        )}
                        <div ref={transcriptEndRef} />
                    </div>
                    {/* Input */}
                    <div className="border-t border-stone-200 p-4">
                        <div className="flex gap-2">
                            <input
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && !sending && sendText()}
                                placeholder={sending ? 'Waiting for avatar reply…' : 'Type what you say next…'}
                                disabled={!connected || sending}
                                className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:border-violet-400 disabled:bg-stone-100"
                            />
                            <button
                                onClick={sendText}
                                disabled={!connected || !input.trim() || sending}
                                className="px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold flex items-center gap-1.5 hover:bg-stone-800 disabled:opacity-50"
                            >
                                <Send className="w-4 h-4" />
                                Send
                            </button>
                        </div>
                        <div className="mt-1.5 text-[10px] text-stone-400">
                            Tip: press Enter to send. Web version uses text for testing — the desktop Electron app supports live voice.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
